import { createContext, useContext, useMemo, type ReactNode } from "react";

/**
 * Cienki adapter tłumaczeń dla @asad/crane-ui.
 *
 * PO CO. Jedenaście komponentów pakietu pokazuje teksty (przyciski paginacji, etykiety
 * modali, stany puste). Są trzy sposoby, żeby to obsłużyć, i dwa z nich są złe:
 *
 *   • zależność od `react-i18next` — app/frontend ma wersję 13, app/prototypes 15,
 *     a pakiet wymuszałby wtedy peer-range przez dwa majory. Gorzej: wiązałby obie
 *     aplikacje obowiązkiem zdefiniowania 32 konkretnych kluczy, bo inaczej UI
 *     renderowałoby surowe `tables.itemsCount`;
 *
 *   • 32 propsy z etykietami — każde wywołanie każdego komponentu musiałoby je podać.
 *
 * Zamiast tego pakiet przyjmuje od aplikacji funkcję `t`. Bez providera używa
 * wbudowanych polskich wartości domyślnych, więc komponenty działają poprawnie także
 * tam, gdzie nikt i18n nie skonfigurował.
 *
 * Sygnatura celowo odpowiada i18next, żeby podpięcie było jednolinijkowe:
 *
 *   const { t } = useTranslation();
 *   <CraneUiI18nProvider t={t}>…</CraneUiI18nProvider>
 */
export type CraneTranslate = (key: string, options?: Record<string, unknown>) => string;

/** Polskie wartości domyślne — używane, gdy aplikacja nie podpięła providera. */
const DEFAULTS: Record<string, string> = {
  "common.deleteConfirmation": "Ta operacja nie może zostać cofnięta. Czy na pewno chcesz kontynuować?",
  "common.deleteQuestion": "Czy na pewno chcesz usunąć?",
  "common.noResults": "Brak wyników",
  "common.notifications": "Powiadomienia",
  "common.ok": "Ok",
  "common.retry": "Ponów",
  "common.scrollToTop": "Przewiń do góry",
  "common.somethingWentWrong": "Coś poszło nie tak, sprawdź dane i spróbuj ponownie",
  "common.warning": "Ostrzeżenie",
  "commonActions.cancel": "Anuluj",
  "commonActions.close": "Zamknij",
  "commonActions.confirm": "Potwierdź",
  "commonActions.create": "Utwórz",
  "commonActions.edit": "Edytuj",
  "commonActions.loading": "Ładowanie",
  "commonActions.save": "Zapisz",
  "commonActions.saving": "Zapisywanie...",
  "formShell.discard": "Porzuć",
  "formShell.discardDescription": "Wprowadzone zmiany nie zostaną zapisane.",
  "formShell.discardTitle": "Porzucić niezapisane zmiany?",
  "formShell.keepEditing": "Wróć do edycji",
  "forms.clearDate": "Wyczyść datę",
  "forms.clearSelection": "Wyczyść wybór",
  "forms.noOptions": "Brak dostępnych pozycji",
  "forms.removeRow": "Usuń wiersz",
  "forms.selectAll": "Zaznacz wszystkie",
  "forms.selectFromList": "Wybierz z listy",
  "tables.clearFilters": "Wyczyść filtry",
  "tables.filters": "Filtry",
  "tables.itemsCount": "{{total}} pozycji",
  "tables.next": "Następna",
  "tables.of": "z",
  "tables.page": "Strona",
  "tables.previous": "Poprzednia",
  "tables.rowActions": "Akcje wiersza",
};

/** Podstawienie `{{zmienna}}` — tyle, ile potrzebują wartości domyślne. */
const interpolate = (template: string, options?: Record<string, unknown>): string =>
  options ? template.replace(/\{\{(\w+)\}\}/g, (whole, name) => (name in options ? String(options[name]) : whole)) : template;

const fallbackTranslate: CraneTranslate = (key, options) => interpolate(DEFAULTS[key] ?? key, options);

const CraneUiI18nContext = createContext<CraneTranslate | null>(null);

export const CraneUiI18nProvider = ({ t, children }: { t: CraneTranslate; children: ReactNode }) => (
  <CraneUiI18nContext.Provider value={t}>{children}</CraneUiI18nContext.Provider>
);

/** Zwraca `t` aplikacji albo wbudowane wartości domyślne. Nigdy nie rzuca. */
export const useCraneT = (): CraneTranslate => {
  const provided = useContext(CraneUiI18nContext);
  return useMemo(() => provided ?? fallbackTranslate, [provided]);
};

/** Klucze, których pakiet oczekuje od aplikacji — przydatne przy uzupełnianiu tłumaczeń. */
export const CRANE_UI_TRANSLATION_KEYS = Object.keys(DEFAULTS);
